import { Skeleton } from "@/components/ui/skeleton"

const card =
	"overflow-hidden rounded-2xl border border-border bg-card shadow-card"

function CardHeading({
	title,
	description,
}: {
	title: string
	description: string
}) {
	return (
		<div className="border-b border-border-subtle px-6 py-5">
			<h2 className="text-section-title font-semibold">{title}</h2>
			<p className="mt-1 text-meta text-muted-foreground">{description}</p>
		</div>
	)
}

export function DashboardTableSkeleton({
	stalled = false,
}: {
	stalled?: boolean
}) {
	const columns = stalled
		? ["Order", "Name", "Order status", "In status since", "Time in status"]
		: ["Order", "Name", "Current status", "Blocker", "Last updated"]
	return (
		<div
			role="status"
			aria-label={
				stalled ? "Loading orders without progress" : "Loading blockers"
			}
			className="[&_[data-slot=skeleton]]:motion-reduce:animate-none"
		>
			<div aria-hidden="true">
				<div className="overflow-x-auto">
					<table className="w-full text-left text-data">
						<thead className="bg-muted text-label uppercase text-subtle-foreground">
							<tr>
								{columns.map((column) => (
									<th
										key={column}
										className="whitespace-nowrap px-6 py-3 font-semibold"
									>
										{column}
									</th>
								))}
							</tr>
						</thead>
						<tbody className="divide-y divide-border-subtle">
							{Array.from({ length: 6 }, (_, row) => (
								<tr key={row}>
									{columns.map((column, index) => (
										<td key={column} className="px-6 py-3">
											<Skeleton
												className={
													index === 0 ? "h-[18px] w-16" : "h-[18px] w-24"
												}
											/>
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
				<div className="flex items-center justify-between gap-3 border-t border-border-subtle px-6 py-3">
					<div className="flex items-center gap-3">
						<Skeleton className="h-4 w-36" />
						{stalled ? <Skeleton className="h-4 w-24" /> : null}
					</div>
					<div className="flex gap-2">
						<Skeleton className="h-8 w-20" />
						<Skeleton className="h-8 w-14" />
					</div>
				</div>
			</div>
		</div>
	)
}

export function DashboardSkeleton() {
	return (
		<div
			role="status"
			aria-label="Loading dashboard"
			aria-busy="true"
			className="space-y-6 font-inter [&_[data-slot=skeleton]]:motion-reduce:animate-none"
		>
			<div aria-hidden="true" className="space-y-6">
				<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
					{[
						"Orders in progress",
						"Active blockers",
						"Active fills on hold",
						"Completed orders",
					].map((title) => (
						<section key={title} className={`${card} p-5`}>
							<div className="flex items-center justify-between gap-3">
								<h2 className="text-body font-medium text-foreground-secondary">
									{title}
								</h2>
								<Skeleton className="size-8 shrink-0 rounded-full" />
							</div>
							<Skeleton className="mt-2 h-8 w-16" />
							<Skeleton className="mt-1 h-4 w-40 max-w-full" />
						</section>
					))}
				</div>
				<div className="grid items-start gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
					<div className="min-w-0 space-y-6">
						<section className={card}>
							<CardHeading
								title="Blockers requiring attention"
								description="Blockers, integration failures and holds · oldest update first"
							/>
							<DashboardTableSkeleton />
						</section>
						<section className={card}>
							<div className="flex flex-wrap items-center justify-between gap-4 border-b border-border-subtle px-6 py-5">
								<div>
									<h2 className="text-section-title font-semibold">
										Orders not progressing
									</h2>
									<p className="mt-1 text-meta text-muted-foreground">
										Active orders in the same order status, longest first.
									</p>
								</div>
								<Skeleton className="ml-auto h-8 w-24" />
							</div>
							<DashboardTableSkeleton stalled />
						</section>
						<section className={card}>
							<CardHeading
								title="Other exceptions"
								description="Active orders · may also appear in the blocker queue"
							/>
							<div className="divide-y divide-border-subtle px-6">
								{[
									"Failed payments",
									"Lab failures or rejected results",
									"Pharmacy failures",
								].map((label) => (
									<div
										key={label}
										className="flex items-center justify-between gap-3 py-4 text-data text-foreground-secondary"
									>
										<span>{label}</span>
										<Skeleton className="h-[18px] w-8" />
									</div>
								))}
							</div>
						</section>
					</div>
					<div className="min-w-0 space-y-6">
						<section className={card}>
							<CardHeading
								title="Order progress"
								description="Active orders by current status group"
							/>
							<div className="space-y-4 p-6">
								{[
									"Intake",
									"Payment",
									"Labs",
									"Consultation",
									"Fulfillment",
									"Refills",
								].map((label) => (
									<div
										key={label}
										className="grid grid-cols-[7rem_1fr_2.5rem] items-center gap-3 text-data text-foreground-secondary"
									>
										<span>{label}</span>
										<Skeleton className="h-2 w-full rounded-full" />
										<Skeleton className="h-[18px] w-6 justify-self-end" />
									</div>
								))}
							</div>
						</section>
						<section className={card}>
							<CardHeading
								title="Order outcomes"
								description="Terminal workflow statuses · all time"
							/>
							<div className="divide-y divide-border-subtle px-6">
								{[
									"Payment Retry Exhausted",
									"Refill Cancelled",
									"Cancelled",
									"Expired",
									"Completed",
								].map((label) => (
									<div
										key={label}
										className="flex items-center justify-between gap-3 py-3 text-data text-foreground-secondary"
									>
										<span>{label}</span>
										<Skeleton className="h-[18px] w-8" />
									</div>
								))}
							</div>
						</section>
					</div>
				</div>
			</div>
		</div>
	)
}
